import React, { useEffect, useState } from 'react';
import { 
  Box,
  Flex,
  Text,
  Link,
  useToast,
  IconButton,
  Collapse,
  SimpleGrid
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { cqAPI, Auth } from "../../lib/Auth";
import moment from 'moment';

function Payment() {
  const [payments, setPayments] = useState({ transactions: [], installment_payments: [], posters: [], businesses: [] });
  const toast = useToast();

  const fetchPayments = async () => {
    try {
      const response = await cqAPI.get('/pp/payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments numbers:', error);
      toast({
        title: "Error fetching payments.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);
  
  const Transaction = React.memo(({
    transaction, posters, businesses, installmentPayments
  }) => {
    const toast = useToast();

    const cancelInstallment = async (id) => {
      const response = await cqAPI.post('/payments/cancel_installment', { id });
      if (response.type === 'error') {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      await fetchPayments();
    };

    const {
      _id: transaction_id, description: desc, created: time, amount, fee, doxi_fee, 
      source, installment, total_installment, downpayment, number_of_installments, 
      installment_ids, installment_failed, installment_stopped, dispute, posted_by, bid, id
    } = transaction;

    const [showInstallments, setShowInstallments] = useState(false);

    const fees = [fee, doxi_fee];
    const card = source && source.last4 ? `**** **** **** ${source.last4}` : '';

    const total_amount = installment ? (total_installment + downpayment) : amount;
    const downpayment_refunded = (installmentPayments || []).find(e => (e.type === 'refund') && (e.original_transaction === transaction_id)) ? true : false;
    const actual_amount = installment ? 
      (total_installment / number_of_installments * ((installment_ids || []).length - (downpayment_refunded ? 1 : 0)) + (downpayment_refunded ? 0 : downpayment)) : 
      amount;

    const posterName = posters.find(e => e._id === posted_by)?.profile?.name || 'unknown';
    const businessName = businesses.find(e => e._id === bid)?.name || '';

    const installmentTransactions = installment_ids && installment_ids.length > 0 
      ? installment_ids.map(instId => installmentPayments.find(ip => String(ip._id) === String(instId))) 
      : [];
    
    if (transaction.installment){
      installmentTransactions.push({
        ...transaction,
        installment : false,
        description : 'Downpayment'
      })
    }

    return (
      <Box borderWidth="1px" borderRadius="lg" width="100%" className="actionableItem" mb={4}>
        <Flex direction="column" p={4} justify="space-between" height="100%">
          {/* Top section with date and description on the left, amount and card on the right */}
          <Flex justify="space-between" align="flex-start" textAlign="left">
            {/* Left side: Date and Description */}
            <Box>
              <Text fontSize="sm">Date: <b>{moment(time).format('ll')}</b></Text>
              <Text fontSize="sm">Description: <b>{desc}</b></Text>
              {!Auth.user.isPatient && (
                <Text fontSize="xs">
                  (by {posterName} for {businessName})
                </Text>
              )}
            </Box>
            {/* Right side: Amount and Card */}
            <Box textAlign="right">
              {/* Amount */}
              {fees.find(x => x !== 0) && actual_amount < 0 ? (
                <Text fontSize="sm">
                  <Link title={`Credit: $${(-actual_amount / 100).toFixed(2)}\nFee: $${((fees[0] + fees[1]) / 100).toFixed(2)}`}>
                    &nbsp;+
                  </Link>
                  {!dispute && (
                    <Link>
                      <i className="fa fa-undo" style={{ paddingLeft: 5 }}></i>
                    </Link>
                  )}
                </Text>
              ) : (
                <Text fontSize="sm">
                  Amount: <b>${(total_amount / 100).toFixed(2)}</b>
                </Text>
              )}
              {/* Card */}
              <Text fontSize="sm">Card: <b>{card}</b></Text>
              {/* Cancel link if applicable */}
              {installment && number_of_installments !== installment_ids.length && !installment_stopped && (
                <Link onClick={() => cancelInstallment(id)} color="red.500" data-stripe-id={transaction_id}>
                  Cancel
                </Link>
              )}
            </Box>
          </Flex>
          {/* Show Installments button if applicable */}
          {installment && (
            <Flex justify="flex-end" mt={2}>
              <IconButton
                size="sm"
                onClick={() => setShowInstallments(!showInstallments)}
                icon={showInstallments ? <FaChevronUp /> : <FaChevronDown />}
                aria-label={showInstallments ? 'Collapse installments' : 'Expand installments'}
              />
            </Flex>
          )}
          {/* Installments section */}
          <Collapse in={showInstallments} animateOpacity>
            {installmentTransactions.map((installmentTransaction, index) => (
              installmentTransaction && (
                <Box pl={4} pt={2} key={index}>
                  <Transaction 
                    transaction={installmentTransaction} 
                    posters={posters} 
                    businesses={businesses} 
                    installmentPayments={installmentPayments}
                  />
                </Box>
              )
            ))}
          </Collapse>
        </Flex>
      </Box>
    );
  });

  const { transactions, installment_payments, posters, businesses } = payments;

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
      <SimpleGrid columns={{ base: 1, md: 3, lg: 1 }}>
        {transactions.filter(transaction=>!installment_payments.find(tr=>tr.id === transaction.id)).map((transaction, index) => (
          <Transaction
            key={index}
            transaction={transaction}
            posters={posters}
            businesses={businesses}
            installmentPayments={installment_payments}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default Payment;
